'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import { Avatar } from '@/types/avatar';

interface AvatarPreviewProps {
  avatar: Avatar;
  size?: 'sm' | 'md' | 'lg';
  showActions?: boolean;
  onView3D?: () => void;
  onSetDefault?: () => void;
  onDelete?: () => void;
  isDefault?: boolean;
  className?: string;
}

const sizeClasses = {
  sm: 'w-16 h-16',
  md: 'w-24 h-24', 
  lg: 'w-32 h-32'
};

export default function AvatarPreview({
  avatar,
  size = 'md',
  showActions = false,
  onView3D,
  onSetDefault,
  onDelete,
  isDefault = false,
  className = ''
}: AvatarPreviewProps) {
  const [showActions3D, setShowActions3D] = useState(false);
  const [imageError, setImageError] = useState(false);

  return (
    <div 
      className={`relative group ${className}`}
      onMouseEnter={() => setShowActions3D(true)}
      onMouseLeave={() => setShowActions3D(false)}
    >
      {/* Avatar Image */}
      <div className={`${sizeClasses[size]} relative rounded-lg overflow-hidden bg-gray-100`}>
        {!imageError && avatar.previewUrl ? (
          <Image
            src={avatar.previewUrl}
            alt={avatar.name}
            fill
            className="object-cover"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
        )}

        {/* Default Badge */}
        {isDefault && (
          <div className="absolute top-1 right-1 bg-green-500 text-white text-xs px-1.5 py-0.5 rounded">
            Default
          </div>
        )}

        {/* Hover Overlay */}
        {showActions && showActions3D && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center transition-all duration-200">
            <button
              onClick={onView3D}
              className="bg-white/90 hover:bg-white text-gray-800 px-3 py-1.5 rounded text-sm font-medium transition-all duration-200"
            >
              View 3D
            </button>
          </div>
        )}
      </div>

      {/* Avatar Name */}
      <div className="mt-2 text-center">
        <p className="text-sm font-medium text-gray-900 truncate">{avatar.name}</p>
        {avatar.createdAt && (
          <p className="text-xs text-gray-500">
            {new Date(avatar.createdAt).toLocaleDateString()}
          </p>
        )}
      </div>

      {/* Action Buttons */}
      {showActions && (
        <div className="mt-2 flex gap-1 justify-center">
          {!isDefault && onSetDefault && (
            <button
              onClick={onSetDefault}
              className="text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 px-2 py-1 rounded transition-colors"
            >
              Set Default
            </button>
          )}
          {onDelete && (
            <button
              onClick={onDelete}
              className="text-xs bg-red-100 hover:bg-red-200 text-red-700 px-2 py-1 rounded transition-colors"
            >
              Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
}